/**
 * useShapeMorph — React hook for shape morphing animations.
 *
 * Manages the Morph instance and animated progress value, returning a
 * CSS clip-path string that updates with every animation frame.
 *
 * @example
 * const { clipPath, handlers } = useShapeMorph({
 *   shape: "circle",
 *   morphTo: "flower",
 *   morphOn: "hover",
 *   width: 200,
 *   height: 200,
 * });
 *
 * return <div style={{ clipPath }} {...handlers}>{children}</div>;
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveShape } from "../../shapes/catalog/md3-expressive-shapes";
import { Morph } from "../../shapes/morph/morph";
import {
	interpolatePath,
	toClipPath,
} from "../../shapes/render/shape-rendering";
import type {
	MorphTrigger,
	ShapeMorphOptions,
	ShapeRef,
	UseShapeMorphResult,
} from "./types";

interface UseShapeMorphOptions {
	shape: ShapeRef;
	morphTo?: ShapeRef;
	morphOn?: MorphTrigger;
	morphOptions?: ShapeMorphOptions;
	scrollProgress?: number;
	width: number;
	height: number;
	disabled?: boolean;
}

/** Returns true if the user prefers reduced motion. */
function prefersReducedMotion(): boolean {
	if (typeof window === "undefined") return false;
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Hook for shape morphing with animated clip-path output.
 */
export function useShapeMorph({
	shape,
	morphTo,
	morphOn = "hover",
	morphOptions,
	scrollProgress,
	width,
	height,
	disabled = false,
}: UseShapeMorphOptions): UseShapeMorphResult {
	const duration = morphOptions?.duration ?? 0.3;
	const easing = morphOptions?.easing ?? "ease-in-out";
	const easingFunction = useMemo(() => getEasingFunction(easing), [easing]);

	// ─── Memoize shape instances ─────────────────────────────────────────
	const startShape = useMemo(() => resolveShape(shape), [shape]);
	const endShape = useMemo(
		() => (morphTo ? resolveShape(morphTo) : null),
		[morphTo],
	);

	// ─── Memoize Morph instance ──────────────────────────────────────────
	const morph = useMemo(
		() => (endShape ? new Morph(startShape, endShape) : null),
		[startShape, endShape],
	);

	// ─── Animation state ─────────────────────────────────────────────────
	const [isActive, setIsActive] = useState(false);
	const [clipPath, setClipPath] = useState<string>(() =>
		toClipPath(startShape, width, height),
	);

	// Animation tracking
	const animRef = useRef<number | null>(null);
	const startTimeRef = useRef<number>(0);
	const startProgressRef = useRef<number>(0);
	const targetProgressRef = useRef<number>(0);
	const currentProgressRef = useRef<number>(0);

	// ─── Update clip-path when dimensions change ──────────────────────────
	useEffect(() => {
		if (morph) {
			setClipPath(
				interpolatePath(morph, currentProgressRef.current, width, height),
			);
		} else {
			setClipPath(toClipPath(startShape, width, height));
		}
	}, [width, height, startShape, morph]);

	// ─── Scroll-driven mode ──────────────────────────────────────────────
	useEffect(() => {
		if (morphOn !== "scroll" || !morph || scrollProgress === undefined) return;
		const p = Math.max(0, Math.min(1, scrollProgress));
		currentProgressRef.current = p;
		setClipPath(interpolatePath(morph, p, width, height));
	}, [scrollProgress, morphOn, morph, width, height]);

	// ─── Animation loop ──────────────────────────────────────────────────
	const animate = useCallback(
		(timestamp: number) => {
			if (!morph) return;
			const elapsed = timestamp - startTimeRef.current;
			const durationMs = duration * 1000;
			const rawT = Math.min(elapsed / durationMs, 1);
			const ease = easingFunction(rawT);
			const progress =
				startProgressRef.current +
				(targetProgressRef.current - startProgressRef.current) * ease;
			currentProgressRef.current = progress;
			setClipPath(interpolatePath(morph, progress, width, height));

			if (rawT < 1) {
				animRef.current = requestAnimationFrame(animate);
			} else {
				animRef.current = null;
			}
		},
		[morph, duration, easingFunction, width, height],
	);

	const startAnimation = useCallback(
		(toProgress: number) => {
			if (!morph) return;
			if (prefersReducedMotion()) {
				currentProgressRef.current = toProgress;
				setClipPath(interpolatePath(morph, toProgress, width, height));
				return;
			}
			if (animRef.current !== null) cancelAnimationFrame(animRef.current);
			startProgressRef.current = currentProgressRef.current;
			targetProgressRef.current = toProgress;
			startTimeRef.current = performance.now();
			animRef.current = requestAnimationFrame(animate);
		},
		[morph, animate, width, height],
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (animRef.current !== null) cancelAnimationFrame(animRef.current);
		};
	}, []);

	// ─── Actions ─────────────────────────────────────────────────────────
	const activate = useCallback(() => {
		if (disabled || !morph) return;
		setIsActive(true);
		startAnimation(1);
	}, [disabled, morph, startAnimation]);

	const deactivate = useCallback(() => {
		if (disabled || !morph) return;
		setIsActive(false);
		startAnimation(0);
	}, [disabled, morph, startAnimation]);

	const setProgress = useCallback(
		(progress: number) => {
			if (!morph) return;
			const p = Math.max(0, Math.min(1, progress));
			currentProgressRef.current = p;
			setIsActive(p >= 0.5);
			setClipPath(interpolatePath(morph, p, width, height));
		},
		[morph, width, height],
	);

	// ─── Event handlers ──────────────────────────────────────────────────
	const handlers = useMemo(() => {
		if (!morphTo || disabled) return {};
		if (morphOn === "hover") {
			return { onMouseEnter: activate, onMouseLeave: deactivate };
		}
		if (morphOn === "click") {
			return {
				onClick: () => {
					if (isActive) deactivate();
					else activate();
				},
			};
		}
		if (morphOn === "focus") {
			return { onFocus: activate, onBlur: deactivate };
		}
		return {};
	}, [morphTo, disabled, morphOn, activate, deactivate, isActive]);

	return { clipPath, isActive, activate, deactivate, setProgress, handlers };
}

// ─── Easing ──────────────────────────────────────────────────────────────────

function easeCubicInOut(t: number): number {
	return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function cubicBezier(
	x1: number,
	y1: number,
	x2: number,
	y2: number,
): (t: number) => number {
	return (t: number) => {
		if (t === 0 || t === 1) return t;

		// Newton-Raphson method for solving x(t_param) = t
		let tParam = t;
		for (let i = 0; i < 8; i++) {
			const x =
				3 * (1 - tParam) ** 2 * tParam * x1 +
				3 * (1 - tParam) * tParam ** 2 * x2 +
				tParam ** 3;
			const dx =
				3 * (1 - tParam) ** 2 * x1 +
				6 * (1 - tParam) * tParam * (x2 - x1) +
				3 * tParam ** 2 * (1 - x2);
			if (Math.abs(dx) < 1e-6) break;
			tParam -= (x - t) / dx;
		}

		// Calculate y(t_param)
		return (
			3 * (1 - tParam) ** 2 * tParam * y1 +
			3 * (1 - tParam) * tParam ** 2 * y2 +
			tParam ** 3
		);
	};
}

function getEasingFunction(easing: string | number[]): (t: number) => number {
	if (Array.isArray(easing)) {
		if (easing.length === 4) {
			const [x1, y1, x2, y2] = easing;
			return cubicBezier(x1, y1, x2, y2);
		}
		return easeCubicInOut;
	}

	switch (easing) {
		case "linear":
			return (t) => t;
		case "ease-in":
			return (t) => t * t * t;
		case "ease-out":
			return (t) => 1 - (1 - t) ** 3;
		default:
			return easeCubicInOut;
	}
}
